import { assertExhaustive } from 'backend-client-web';
import { Login, LoginUiType, Unprotect, getValueWithPlaceholder } from 'persistable-types';

import getImplicitLoginWebsites from './getImplicitLoginWebsites';
import { normalizeWebsitePattern } from './normalizeLogin/normalizeWebsitePattern';

type DeepPartial<T> = {
  [P in keyof T]?: unknown extends T[P] ? unknown : DeepPartial<T[P]>;
};

type DeepPartialForKeys<T, K extends keyof T> = Partial<Omit<T, K>> & DeepPartial<Pick<T, K>>;

export type LoginForDisplay = DeepPartialForKeys<
  Unprotect<Omit<Login, 'history'>, unknown>,
  'creditCard' | 'elsterCertificate'
>;

/**
 * Get the label of a login. This is used to differentiate logins in contexts where the user already
 * knows the scope for which logins are shown. The canonical example is the buttons in the login
 * overlay.
 */
export function getLoginDisplayLabel(login: LoginForDisplay) {
  if (login.title?.trim()) {
    return login.title.trim();
  }
  if (login.uiType === LoginUiType.creditCard && login.creditCard?.displayType?.trim()) {
    return login.creditCard.displayType.trim();
  }
  if (login.uiType === LoginUiType.wifi && login.wifiSsid?.trim()) {
    return login.wifiSsid.trim();
  }
  if (login.username?.trim()) {
    return login.username.trim();
  }
  if (
    login.uiType === LoginUiType.elsterCertificate &&
    login.elsterCertificate?.subjectCN?.trim()
  ) {
    // For elster certificates, use certificate number as only as a fallback after the username
    // If a username is set it's more distinguishing so it should be preferred
    return login.elsterCertificate.subjectCN.trim();
  }
  return undefined;
}

/**
 * Gets the scope of a login for display. This is the app(s)/website(s) that use the login.
 */
export function getLoginDisplayScope(
  login: LoginForDisplay,
  { noScopeEntryCount }: { noScopeEntryCount?: boolean } = {},
) {
  const implicitWebsites = getImplicitLoginWebsites(login);
  const androidApps = login.androidApps ?? [];
  const websites = login.websites?.filter((w) => w.trim()) ?? [];
  const scope =
    login.scope?.flatMap((e) => {
      const entry = getValueWithPlaceholder(e);
      if (entry.kind === 'unknown') {
        return [];
      }
      return entry;
    }) ?? [];

  let displayWebsite: string | undefined;
  if (implicitWebsites.length) {
    displayWebsite = implicitWebsites[0].website;
  } else if (scope.length) {
    switch (scope[0].kind) {
      case 'website':
        displayWebsite = normalizeWebsitePattern(scope[0].pattern, { forDisplay: true });
        break;
      case 'androidApp':
        displayWebsite = scope[0].displayName;
        break;
      case 'windowsDesktopApp':
        displayWebsite = scope[0].exeName;
        break;
      default:
        assertExhaustive(scope[0]);
    }
  } else if (androidApps.length) {
    displayWebsite = androidApps[0].displayName;
  } else if (websites.length) {
    displayWebsite = normalizeWebsitePattern(websites[0], { forDisplay: true });
  }

  const length = implicitWebsites.length + scope.length + androidApps.length + websites.length;
  if (!noScopeEntryCount && displayWebsite !== undefined && length > 1) {
    displayWebsite += ` (+${length - 1})`;
  }

  return displayWebsite;
}
export interface LoginDisplayInfo {
  // undefined indicates that a uiType-specific fallback should be used
  headline: string | undefined;
  // undefined indicates that no subheadline should be shown
  subheadline?: string;
  subheadlineMonospace?: boolean;
}

export interface LoginDisplayOptions {
  noScopeEntryCount?: boolean;
}

export function getLoginDisplayInfo(
  login: LoginForDisplay,
  { noScopeEntryCount }: LoginDisplayOptions = {},
): LoginDisplayInfo {
  const displayName = login.title?.trim() || undefined;
  const displayHeadline = login.displayHeadline?.trim() || undefined;

  if (displayHeadline && displayName) {
    return {
      headline: displayHeadline,
      subheadline: displayName,
    };
  }

  // We used to use the display name (title field) as headline for non-login ui types, so there'll
  // be logins in the wild expecting that behavior. We prefer the new display headline, but fall
  // back to display name to support these legacy logins.
  const displayHeadlineOrName = displayHeadline || displayName;

  if (login.uiType === LoginUiType.creditCard) {
    const cardNumber = login.creditCard?.displayMaskedNumber?.trim() || undefined;
    if (cardNumber) {
      return {
        headline: displayHeadlineOrName || login.creditCard?.displayType?.trim() || undefined,
        subheadline: cardNumber,
        subheadlineMonospace: true,
      };
    }
    return {
      headline: displayHeadlineOrName || login.creditCard?.displayType?.trim() || undefined,
    };
  }

  if (login.uiType === LoginUiType.wifi) {
    if (displayHeadlineOrName) {
      return {
        headline: displayHeadlineOrName,
        subheadline: login.wifiSsid?.trim() || undefined,
      };
    }
    return {
      headline: login.wifiSsid?.trim() || undefined,
    };
  }

  if (login.uiType === LoginUiType.note) {
    return {
      headline: displayHeadlineOrName,
      subheadline: login.note?.split('\n')[0].substring(0, 100).trim() || undefined,
    };
  }

  if (login.uiType === LoginUiType.elsterCertificate && login.elsterCertificate) {
    const sources = getLoginDisplayScope(login, { noScopeEntryCount });
    const accountNumber = login.elsterCertificate.subjectCN?.trim();
    const subheadline = displayName || login.username?.trim() || accountNumber || undefined;
    return {
      headline: displayHeadline || sources,
      subheadline,
      subheadlineMonospace: subheadline === accountNumber,
    };
  }

  if (displayHeadline) {
    return {
      headline: displayHeadline,
      subheadline: displayName || login.username?.trim() || undefined,
    };
  }

  const sources = getLoginDisplayScope(login, { noScopeEntryCount });
  if (sources) {
    return {
      headline: sources,
      subheadline: displayName || login.username?.trim() || undefined,
    };
  }

  if (displayName) {
    return { headline: displayName };
  }

  const username = login.username?.trim();
  if (username) {
    return { headline: username };
  }

  return {
    headline: undefined,
  };
}
